/**
 * AICoachPane - Context-aware AI coaching
 * Adapts guidance based on mode and phase
 */

import { useState, useRef, useEffect } from 'react'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { useWorkspaceCoach } from '@/hooks/useWorkspaceCoach'
import { Button } from '@/components/ui/button'
import { Sparkles, Send, Loader2, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

export function AICoachPane() {
  const { workspaceContext } = useWorkspaceStore()
  const { messages, isLoading, isStreaming, error, sendMessage, isConfigured } = useWorkspaceCoach()

  const [input, setInput] = useState('')
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<string>>(new Set())
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Handle send
  const handleSend = async () => {
    if (!input.trim() || isLoading || isStreaming) return

    const content = input.trim()
    setInput('')

    await sendMessage(content, {
      mode: workspaceContext.mode,
      phaseId: workspaceContext.phaseId,
      journeyMode: workspaceContext.journeyMode,
    })
  }

  // Handle key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Toggle suggestion selection
  const toggleSuggestion = (suggestion: string) => {
    setSelectedSuggestions((prev) => {
      const next = new Set(prev)
      if (next.has(suggestion)) {
        next.delete(suggestion)
      } else {
        next.add(suggestion)
      }
      return next
    })
  }

  // Send selected suggestions
  const handleSendSelected = async () => {
    if (selectedSuggestions.size === 0) return

    const items = Array.from(selectedSuggestions)
    const prompt =
      items.length === 1
        ? `Tell me more about ${items[0]}`
        : items.length === 2
        ? `Tell me more about ${items[0]} and ${items[1]}`
        : `Tell me more about ${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`

    setSelectedSuggestions(new Set())
    await sendMessage(prompt, {
      mode: workspaceContext.mode,
      phaseId: workspaceContext.phaseId,
      journeyMode: workspaceContext.journeyMode,
    })
  }

  // Not configured
  if (!isConfigured) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="w-10 h-10 rounded-full bg-warm-100 flex items-center justify-center mb-3">
          <AlertCircle className="w-5 h-5 text-warm-500" />
        </div>
        <p className="text-xs text-text-muted max-w-[280px]">
          Configure your Claude API key in Settings to enable AI coaching
        </p>
      </div>
    )
  }

  const tips = getContextualTips(workspaceContext.mode)

  return (
    <div className="flex flex-col h-full">
      {/* Contextual Tips */}
      {messages.length === 0 && (
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-sm font-medium text-warm-900">
            <Sparkles size={14} style={{ color: 'var(--color-solution)' }} />
            <span>Quick Tips</span>
          </div>

          <div className="space-y-1.5">
            {tips.map((tip, index) => (
              <div
                key={index}
                className="text-xs p-2 rounded bg-warm-100 text-warm-700"
              >
                {tip}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Chat Messages */}
      {messages.length > 0 && (
        <div className="flex-1 overflow-y-auto mb-4 space-y-3 min-h-0">
          {messages.map((message) => (
            <div key={message.id} className="space-y-2">
              <div
                className={cn(
                  'text-xs p-2.5 rounded-lg',
                  message.role === 'user'
                    ? 'bg-warm-100 text-warm-900 ml-4'
                    : 'bg-blue-50 text-blue-900 mr-4'
                )}
              >
                <div className="whitespace-pre-wrap">{message.content}</div>
                {message.isStreaming && (
                  <Loader2 className="w-3 h-3 animate-spin mt-1 text-warm-500" />
                )}
              </div>

              {/* Suggestion Tags */}
              {message.role === 'assistant' &&
                !message.isStreaming &&
                message.suggestions &&
                message.suggestions.length > 0 && (
                  <div className="mr-4 space-y-2">
                    <div className="flex flex-wrap gap-1.5">
                      {message.suggestions.map((suggestion, idx) => (
                        <button
                          key={idx}
                          onClick={() => toggleSuggestion(suggestion)}
                          className={cn(
                            'px-2 py-1 text-xs rounded-full border transition-colors',
                            selectedSuggestions.has(suggestion)
                              ? 'bg-blue-100 border-blue-300 text-blue-900 font-medium'
                              : 'bg-white border-warm-200 text-warm-700 hover:border-warm-300 hover:bg-warm-50'
                          )}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>

                    {/* Confirm Button */}
                    {selectedSuggestions.size > 0 && (
                      <button
                        onClick={handleSendSelected}
                        disabled={isLoading || isStreaming}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Send className="w-3 h-3" />
                        Ask about {selectedSuggestions.size} selected
                      </button>
                    )}
                  </div>
                )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
          {error}
        </div>
      )}

      {/* Chat Input */}
      <div className="space-y-2 mt-auto">
        <label className="text-xs font-medium text-warm-700">
          Ask for guidance
        </label>
        <div className="relative">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask a question about your current work..."
            className="w-full px-3 py-2 pr-10 text-xs border border-warm-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-300 resize-none"
            rows={2}
            disabled={isLoading || isStreaming}
          />
          <Button
            size="sm"
            onClick={handleSend}
            disabled={!input.trim() || isLoading || isStreaming}
            className="absolute right-2 bottom-2 h-6 w-6 p-0 rounded-full"
          >
            {isLoading || isStreaming ? (
              <Loader2 className="h-3 h-3 animate-spin" />
            ) : (
              <Send className="h-3 w-3" />
            )}
          </Button>
        </div>
        <div className="text-xs text-warm-500">
          Enter to send • Shift+Enter for new line
        </div>
      </div>
    </div>
  )
}

function getContextualTips(mode: string): string[] {
  switch (mode) {
    case 'canvas':
      return [
        'Focus on value proposition clarity',
        'Ensure customer segments are specific',
        'Link channels to customer segments',
      ]
    case 'smart-doc':
      return [
        'Extract key entities as you write',
        'Use headers to structure content',
        'Tag customer insights systematically',
      ]
    case 'entities':
      return [
        'Complete required entities first',
        'Add evidence to support claims',
        'Link entities to show relationships',
      ]
    default:
      return [
        'Start with customer discovery',
        'Focus on problem validation',
        'Gather evidence systematically',
      ]
  }
}
